import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  Container,
  Grid,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { LocationOn as LocationOnIcon } from "../../icons";
import { Search as SearchIcon } from "../../icons";
import { Storefront as StorefrontIcon } from "../../icons";
import { db } from "../../firebase";
import { brandColors } from "../../theme";
import { pageContentSx } from "../../pageLayout";
import { T } from "../Translate";
import StoresAppBar from "./StoresAppBar";
import GoogleAdSideRail from "../../components/GoogleAdSideRail";
import GoogleAdInline from "../../components/GoogleAdInline";

import SearchClearAdornment from "../../components/SearchClearAdornment";
// Directorio público de tiendas: se ve sin iniciar sesión (las tiendas son de
// lectura pública en las reglas). Cada tarjeta es un <a href> real para que
// Google pueda seguir los links y rastrear cada tienda (ver también
// sitemap.xml, generado por la función `sitemap`).
const StoresDirectory = () => {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    let active = true;
    db.collection("stores")
      .get()
      .then((snap) => {
        if (!active) return;
        const items = snap.docs
          .map((doc) => ({ slug: doc.id, ...doc.data() }))
          // Tiendas sin nombre (incompletas o de prueba) no se listan.
          .filter((store) => store.storeName)
          .sort((a, b) => (a.storeName || a.slug).localeCompare(b.storeName || b.slug));
        setStores(items);
      })
      .catch((e) => console.error("Error cargando el directorio de tiendas:", e))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const term = searchTerm.trim().toLowerCase();
  const filteredStores = stores.filter((store) =>
    `${store.storeName || ""} ${store.description || ""} ${store.address || ""}`
      .toLowerCase()
      .includes(term)
  );

  return (
    <Box sx={{ bgcolor: brandColors.backgroundDefault, minHeight: "100vh", pb: "80px" }}>
      <StoresAppBar title={<T>Tiendas</T>} subtitle={<T>Tiendas en Notiline</T>} quickNav={false} />
      <Container maxWidth={false} disableGutters sx={{ ...pageContentSx, pt: { xs: 6, md: 10 }, position: "relative" }}>
        {/* AdSense: solo con tiendas en pantalla (no durante la carga ni sin resultados) */}
        {!loading && filteredStores.length > 0 && <GoogleAdSideRail />}
        <Typography variant="h4" component="h1" sx={{ fontWeight: 800, fontSize: { xs: "1.5rem", md: "1.9rem" }, lineHeight: 1.2, mb: 0.5 }}>
          <T>Tiendas en Notiline</T>
        </Typography>
        <Typography sx={{ color: "text.secondary", fontSize: "0.92rem", mb: 2 }}>
          <T>Conocé los negocios que venden en Notiline y sus productos.</T>
        </Typography>

        <TextField
          fullWidth
          size="small"
          placeholder="Buscar tienda..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 2.5, "& .MuiOutlinedInput-root": { borderRadius: 999 } }}
          InputProps={{
            endAdornment: <SearchClearAdornment value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
        />

        {loading ? (
          <Grid container spacing={1.5}>
            {[0, 1, 2, 3].map((i) => (
              <Grid item xs={12} sm={6} key={i}>
                <Skeleton variant="rounded" height={214} sx={{ borderRadius: "18px" }} />
              </Grid>
            ))}
          </Grid>
        ) : filteredStores.length === 0 ? (
          <Stack alignItems="center" spacing={1} sx={{ py: 7, color: "text.secondary" }}>
            <StorefrontIcon sx={{ fontSize: 40, opacity: 0.6 }} />
            <Typography sx={{ color: "text.secondary" }}>
              <T>No hay resultados.</T>
            </Typography>
          </Stack>
        ) : (
          <Grid container spacing={1.5}>
            {filteredStores.map((store) => (
              <Grid item xs={12} sm={6} key={store.slug}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    backgroundColor: brandColors.backgroundPaper,
                    backgroundImage: "none",
                    border: `1px solid ${brandColors.divider}`,
                    borderRadius: "18px",
                    transition: "border-color 0.15s",
                    "&:hover": { borderColor: brandColors.accent },
                  }}
                >
                  <CardActionArea
                    component={RouterLink}
                    to={`/stores/${store.slug}`}
                    sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "stretch", justifyContent: "flex-start" }}
                  >
                    {/* Sin banner, una franja del color de la marca: así todas las
                        tarjetas quedan iguales y el logo siempre se superpone. */}
                    <Box sx={{ height: 104, flexShrink: 0, bgcolor: alpha(brandColors.accent, 0.14) }}>
                      {store.bannerUrl && (
                        <Box
                          component="img"
                          src={store.bannerUrl}
                          alt={store.storeName || store.slug}
                          loading="lazy"
                          sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                        />
                      )}
                    </Box>
                    <Box sx={{ px: 1.75, pb: 1.75 }}>
                      <Avatar
                        src={store.logoUrl || undefined}
                        sx={{
                          width: 56,
                          height: 56,
                          mt: "-28px",
                          mb: 1,
                          bgcolor: brandColors.backgroundPaper,
                          color: brandColors.accent,
                          border: `3px solid ${brandColors.backgroundPaper}`,
                        }}
                      >
                        <StorefrontIcon />
                      </Avatar>
                      <Typography component="h2" noWrap sx={{ fontSize: "1rem", fontWeight: 700, lineHeight: 1.3 }}>
                        {store.storeName || store.slug}
                      </Typography>
                      {store.address && (
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25, color: "text.secondary", minWidth: 0 }}>
                          <LocationOnIcon sx={{ fontSize: 14, flexShrink: 0 }} />
                          <Typography noWrap sx={{ fontSize: "0.76rem", color: "text.secondary" }}>
                            {store.address}
                          </Typography>
                        </Stack>
                      )}
                      {store.description && (
                        <Typography
                          sx={{
                            mt: 0.875,
                            fontSize: "0.84rem",
                            lineHeight: 1.45,
                            color: "text.secondary",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {store.description}
                        </Typography>
                      )}
                    </Box>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
        {!loading && filteredStores.length > 0 && <GoogleAdInline />}
      </Container>
    </Box>
  );
};

export default StoresDirectory;
